import React from 'react'
import {useLocation} from 'react-router-dom'
import {MemberInfoForCompletePage} from '../../../Props/MemberRegisterPageLocationStates'
import {Signboard} from '../../../DTO/SignboardDTO'
import {ContractInfoWithContractDate, ContractMethod} from '../../../Props/ContractInputInfo'
import {ContractInformationContentBlock} from '../../Components/PaymentsRelations/ConfirmContentBlocks/ContractInformationContentBlock'
import {MBMemberError} from '../../../Common/MBMemberError'
import {RegisteredMemberInfoContents} from '../../Components/CompleteContentBlocks/RegisteredMemberInfoContents'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {ExternalLinkButton} from '../../Atoms/Buttons'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {Spacer} from '../../../../Common/Components/Spacer'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {CvsContract} from '../../../DTO/CvsContractDTO'
import {CvsPaymentInfoContentBlock} from '../../Components/PaymentsRelations/ConfirmContentBlocks/CvsPaymentInfoContentBlock'
import {BucketMemberId} from '../../../DTO/BucketMemberId'
import {RegisteredOrganizationContentBlock} from '../../Components/RegisterOrganizationContentBlocks/RegisteredOrganizationContentBlock'
import {RegisteredFreeServiceContentBlock} from '../../Components/RegisterOrganizationContentBlocks/RegisteredFreeServiceContentBlock'
import {useTranslation} from 'react-i18next'

type LocationStateCommon = {
  contractMethod?: ContractMethod
  memberInfo: MemberInfoForCompletePage
  signboard: Signboard
  contractInfoForCompletePage: ContractInfoWithContractDate
  bucketMemberIdList: BucketMemberId[]
}

export type MembersRegisteredPageLocationState = LocationStateCommon & {
  contractMethod: 'credit_card' | 'mobile_carrier'
}

export type MembersRegisteredPageCvsLocationState = LocationStateCommon & {
  contractMethod: 'convenience_store'
  cvsContract: CvsContract
}

export type MembersRegisteredPageFreeBucketLocationState =
  Omit<LocationStateCommon, 'contractInfoForCompletePage'> & {
  contractMethod: 'none'
}

export const MembersRegisteredPage: React.VFC = () => {
  const location = useLocation<MembersRegisteredPageLocationState
    | MembersRegisteredPageCvsLocationState
    | MembersRegisteredPageFreeBucketLocationState>()
  if (!location?.state?.contractMethod) throw new MBMemberError('InvalidOperation')

  const locationState = location.state
  switch (locationState.contractMethod) {
    case 'credit_card':
    case 'mobile_carrier':
      if (isInvalidLocationState(locationState)) {
        throw new MBMemberError('InvalidOperation')
      }
      return <MembersRegisteredPageInner locationState={locationState}/>
    case 'convenience_store':
      return <MembersRegisteredPageForCvs locationState={locationState}/>
    case 'none':
      if (isInvalidLocationState(locationState)) {
        throw new MBMemberError('InvalidOperation')
      }
      return <MembersRegisteredPageForFreeBucket locationState={locationState}/>
    default:
      throw new MBMemberError('InvalidOperation')
  }
}

type MembersRegisteredPageInnerProps = {
  locationState: MembersRegisteredPageLocationState
}

const MembersRegisteredPageInner: React.VFC<MembersRegisteredPageInnerProps> = (
  props,
) => {
  const {locationState} = props
  const {t} = useTranslation('member')

  const {memberInfo, signboard, bucketMemberIdList, contractInfoForCompletePage} = locationState
  return (
    <MembersPageTemplate>
      <PageTitle>{signboard.signupName + ' 新規登録完了'}</PageTitle>

      <StepGuide activity={'signup'} current={'complete'}/>

      <Spacer mb={'16px'}>
        <PIMStack alignItems={'center'}>
          <RegisteredOrganizationContentBlock
            signboard={signboard}
            title={t('新規登録完了.入会バケット情報.見出し')}
            bucketMemberIdList={bucketMemberIdList}
          />
        </PIMStack>
      </Spacer>

      <ContractInformationContentBlock
        title={'契約情報'}
        contractInfoWithContractDate={contractInfoForCompletePage}
        dataTestId={'block-contract-info'}
      />

      <RegisteredMemberInfoContents
        title={t('新規登録完了.登録情報.見出し')}
        topMessage={[t('新規登録完了.登録情報.説明'), '以下の内容で登録しました。']}
        mail={memberInfo.mail}
        familyName={memberInfo.familyName}
        firstName={memberInfo.firstName}
      />

      <Spacer mt={'30px'}>
        <PIMPageActions>
          <ExternalLinkButton
            label={`${signboard.signupReturnPageInfo.name}に移動する`}
            url={signboard.signupReturnPageInfo.url}
          />
        </PIMPageActions>
      </Spacer>
    </MembersPageTemplate>
  )
}

const isInvalidLocationState = (
  locationState: MembersRegisteredPageLocationState | MembersRegisteredPageFreeBucketLocationState,
): boolean => {
  if (!locationState?.signboard?.signupName) {
    return true
  }
  if (!locationState?.memberInfo?.mail) {
    return true
  }
  if (!locationState?.memberInfo?.familyName) {
    return true
  }
  if (!locationState?.memberInfo?.firstName) {
    return true
  }
  if (!(locationState.contractMethod === 'none')) {
    if (!locationState.bucketMemberIdList) {
      return true
    }
    // noinspection RedundantIfStatementJS
    if (!locationState?.contractInfoForCompletePage) {
      return true
    }
  }
  return false
}

type MembersRegisteredPageForCvsProps = {
  locationState: MembersRegisteredPageCvsLocationState
}

const MembersRegisteredPageForCvs: React.VFC<MembersRegisteredPageForCvsProps> = (
  props,
) => {
  const {locationState} = props
  const {t} = useTranslation('member')

  if (!locationState.cvsContract
    || !locationState.memberInfo?.mail
    || !locationState.memberInfo?.familyName
    || !locationState.memberInfo?.firstName
    || !locationState.contractInfoForCompletePage
    || !locationState.signboard?.signupName
    || !locationState.signboard?.signupReturnPageInfo?.name
    || !locationState.signboard?.signupReturnPageInfo?.url
  ) {
    throw new MBMemberError('InvalidOperation')
  }

  const {
    memberInfo,
    signboard,
    contractInfoForCompletePage,
    cvsContract,
    bucketMemberIdList,
  } = locationState

  return (
    <MembersPageTemplate>
      <PageTitle>{signboard.signupName + ' 新規登録完了'}</PageTitle>

      <StepGuide activity={'signup'} current={'complete'}/>

      <Spacer mb={'16px'}>
        <PIMStack alignItems={'center'}>
          <RegisteredOrganizationContentBlock
            signboard={signboard}
            title={t('新規登録完了.入会バケット情報.見出し コンビニ')}
            bucketMemberIdList={bucketMemberIdList}
            isCvsPayment={true}
          />
        </PIMStack>
      </Spacer>

      <CvsPaymentInfoContentBlock
        title={'お支払い情報'}
        contractInfoWithContractDate={contractInfoForCompletePage}
        dataTestId={'block-contract-info'}
        cvsContract={cvsContract}
        signboard={signboard}
      />

      <RegisteredFreeServiceContentBlock
        freeServices={signboard.freeServices}
        bucketMemberIdList={bucketMemberIdList}
      />

      <RegisteredMemberInfoContents
        title={t('新規登録完了.登録情報.見出し')}
        topMessage={[t('新規登録完了.登録情報.説明'), '以下の内容で登録しました。']}
        mail={memberInfo.mail}
        familyName={memberInfo.familyName}
        firstName={memberInfo.firstName}
      />

      <Spacer mt={'30px'}>
        <PIMPageActions>
          <ExternalLinkButton
            label={`${signboard.signupReturnPageInfo.name}に移動する`}
            url={signboard.signupReturnPageInfo.url}
          />
        </PIMPageActions>
      </Spacer>
    </MembersPageTemplate>
  )
}

type MembersRegisteredPageForFreeBucketProps = {
  locationState: MembersRegisteredPageFreeBucketLocationState
}
const MembersRegisteredPageForFreeBucket: React.VFC<MembersRegisteredPageForFreeBucketProps> = (
  {
    locationState,
  },
) => {
  const {memberInfo, signboard, bucketMemberIdList} = locationState
  const {t} = useTranslation('member')
  return (
    <MembersPageTemplate>
      <PageTitle>{signboard.signupName + ' 新規登録完了'}</PageTitle>

      <StepGuide activity={'signup'} current={'complete'}/>

      <Spacer mb={'16px'}>
        <PIMStack alignItems={'center'}>
          <RegisteredOrganizationContentBlock
            signboard={signboard}
            title={t('新規登録完了.入会バケット情報.見出し')}
            bucketMemberIdList={bucketMemberIdList}
          />
        </PIMStack>
      </Spacer>

      <RegisteredMemberInfoContents
        title={t('新規登録完了.登録情報.見出し')}
        topMessage={[t('新規登録完了.登録情報.説明'), '以下の内容で登録しました。']}
        mail={memberInfo.mail}
        familyName={memberInfo.familyName}
        firstName={memberInfo.firstName}
      />

      <Spacer mt={'30px'}>
        <PIMPageActions>
          <ExternalLinkButton
            label={`${signboard.signupReturnPageInfo.name}に移動する`}
            url={signboard.signupReturnPageInfo.url}
          />
        </PIMPageActions>
      </Spacer>
    </MembersPageTemplate>
  )
}
